import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { playNotificationSound, isSoundMuted, toggleSoundMuted } from '../../utils/notificationSound';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [muted, setMuted] = useState(isSoundMuted());
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`);
      const list = res.data.notifications || [];
      setNotifications(list);
      setUnreadCount(res.data.unread_count || 0);
      // Play a chime when NEW unread notifications arrive (works across every backend path)
      const unreadIds = list.filter(n => !n.is_read && !n.read).map(n => n.id);
      const fresh = unreadIds.filter(id => !seenIdsRef.current.has(id));
      if (initializedRef.current && fresh.length > 0) {
        playNotificationSound();
      }
      list.forEach(n => seenIdsRef.current.add(n.id));
      initializedRef.current = true;
    } catch (e) {
      console.error('NotificationBell: failed to fetch notifications', e);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Listen for WebSocket notifications
  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.id}_notif`);
    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification') {
          seenIdsRef.current.add(data.notification.id);
          setUnreadCount(prev => prev + 1);
          setNotifications(prev => [data.notification, ...prev].slice(0, 10));
          
          // Show in-app toast notification
          const { toast } = await import('sonner');
          toast.info(data.notification.title || 'New Notification', {
            description: data.notification.message || '',
            duration: 5000,
          });
          playNotificationSound();
        } else if (data.type === 'support_message' && data.message?.sender_type === 'staff') {
          // New support reply from staff -> chime + toast
          const { toast } = await import('sonner');
          toast.info('New message from Support', {
            description: data.message.content || '',
            duration: 5000,
          });
          playNotificationSound();
        }
      } catch (e) {
        console.warn('NotificationBell: failed to parse websocket message', e);
      }
    };
    return () => ws.close();
  }, [user?.id]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) {
      console.error('NotificationBell: failed to mark all notifications as read', e);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await axios.put(`${API}/notifications/${notification.id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('NotificationBell: failed to mark notification as read', e);
    }
    
    setShowDropdown(false);
    
    // Navigate based on notification type and user role
    const type = notification.type || '';
    const refId = notification.reference_id;
    const isProvider = user?.role === 'provider';
    const isAdmin = user?.role === 'admin' || user?.role === 'manager';
    
    // Booking-related notifications - navigate to specific booking if refId exists
    if (type.includes('booking') || type === 'new_booking' || type === 'booking_update' || type === 'booking_completed' || type === 'booking_cancelled') {
      if (refId) {
        // Navigate to specific booking details
        navigate(`/booking/${refId}`);
        return;
      }
      if (isProvider) {
        navigate(`/provider/jobs`);
      } else if (isAdmin) {
        navigate(`/admin/bookings`);
      } else {
        navigate(`/bookings`);
      }
      return;
    }
    
    // Chat messages - navigate to booking chat
    if (type === 'chat_message') {
      if (refId) {
        navigate(`/booking/${refId}`);
        return;
      }
      if (isProvider) {
        navigate(`/provider/jobs`);
      } else {
        navigate(`/bookings`);
      }
      return;
    }
    
    // Document verification
    if (type.includes('document')) {
      if (isProvider) {
        navigate(`/provider/profile`);
      } else if (isAdmin) {
        navigate(`/admin/providers`);
      }
      return;
    }
    
    // Wallet/payment notifications
    if (type.includes('wallet') || type.includes('payment') || type.includes('topup') || type === 'commission_deduction') {
      if (isProvider) {
        navigate(`/provider/earnings`);
      } else {
        navigate(`/wallet`);
      }
      return;
    }
    
    // Review notifications
    if (type.includes('review')) {
      if (refId) {
        navigate(`/booking/${refId}`);
        return;
      }
      if (isProvider) {
        navigate(`/provider/reviews`);
      } else {
        navigate(`/bookings`);
      }
      return;
    }
    
    // Report notifications - admin goes to user reports page
    if (type.includes('report') || type.includes('user_report')) {
      if (isAdmin) {
        navigate(`/admin/user-reports`);
      }
      return;
    }
    
    // Dispute notifications
    if (type.includes('dispute')) {
      if (isAdmin) {
        navigate(`/admin/disputes`);
      }
      return;
    }
    
    // Provider verification notifications
    if (type.includes('verification') || type.includes('provider_approved') || type.includes('provider_pending')) {
      if (isAdmin) {
        navigate(`/admin/providers`);
      } else if (isProvider) {
        navigate(`/provider/profile`);
      }
      return;
    }
    
    // Withdrawal notifications
    if (type.includes('withdrawal') || type.includes('payout')) {
      if (isAdmin) {
        navigate(`/admin/withdrawals`);
      } else if (isProvider) {
        navigate(`/provider/earnings`);
      }
      return;
    }
    
    // User management notifications
    if (type.includes('user_banned') || type.includes('user_flagged') || type.includes('new_user')) {
      if (isAdmin) {
        navigate(`/admin/users`);
      }
      return;
    }
    
    // Warning notifications (e.g., low wallet balance)
    if (type === 'warning') {
      if (isProvider) {
        navigate(`/provider/earnings`);
      }
      return;
    }
    
    // Default: go to home
    if (isProvider) {
      navigate(`/provider/home`);
    } else if (isAdmin) {
      navigate(`/admin`);
    } else {
      navigate(`/home`);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setShowDropdown(!showDropdown); if (!showDropdown) fetchNotifications(); }}
        className="relative w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
          <div className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-12 w-auto sm:w-80 max-w-[calc(100vw-1rem)] sm:max-w-80 bg-white rounded-xl shadow-xl border border-stone-200 z-50 max-h-96 overflow-hidden" data-testid="notification-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMuted(toggleSoundMuted())}
                  className="text-slate-400 hover:text-slate-600 p-1"
                  title={muted ? 'Unmute notification sound' : 'Mute notification sound'}
                  data-testid="notification-sound-toggle"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-amber-600 font-medium" data-testid="mark-all-read-btn">
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-72">
              {notifications.length === 0 ? (
                <p className="text-center py-8 text-slate-400 text-sm">No notifications yet</p>
              ) : (
                notifications.map(n => (
                  <button 
                    key={n.id} 
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-stone-50 transition-colors ${!n.is_read ? 'bg-amber-50/50' : ''}`}
                  >
                    <p className="text-sm font-medium text-slate-900 line-clamp-1">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatTime(n.created_at)}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
